import { Bold, Italic, Brain, Paperclip } from "lucide-react";
import { Button } from "@/components/ui/button";
import data from '@emoji-mart/data'
import Picker from '@emoji-mart/react'
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useRef } from "react";
import { convertToUnicode } from "@/utils/textFormatting";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface FormattingToolbarProps {
  onBold: () => void;
  onItalic: () => void;
  onEmojiSelect: (emoji: any) => void;
  onAIClick: () => void;
  onImageUpload: (file: File) => void;
  onVideoUpload?: (file: File) => void;
}

export const FormattingToolbar = ({
  onBold,
  onItalic,
  onEmojiSelect,
  onAIClick,
  onImageUpload,
  onVideoUpload = () => {},
}: FormattingToolbarProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check file type and size
    const isVideo = file.type.startsWith('video/');
    const isImage = file.type.startsWith('image/');
    
    // Video validation
    if (isVideo) {
      const isValidVideo = file.type === 'video/mp4' || file.type === 'video/quicktime';
      if (!isValidVideo) {
        alert('Please upload only MP4 or MOV video files.');
        return;
      }
      // Supabase's limit is 50MB
      if (file.size > 50 * 1024 * 1024) {
        alert('Video file size must be less than 50 MB.');
        return;
      }
      onVideoUpload(file);  // Just pass the file to parent
    } 
    // Image validation
    else if (isImage) {
      onImageUpload(file);
    } else {
      alert('Please upload only image or video files.');
    }
  };

  const handleFormat = (style: 'bold' | 'italic') => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const selectedText = range.toString();
    if (!selectedText) return;

    const formattedText = convertToUnicode(selectedText, style);
    
    const span = document.createElement('span');
    span.textContent = formattedText;
    
    range.deleteContents();
    range.insertNode(span);

    // Trigger the appropriate callback
    if (style === 'bold') onBold();
    else onItalic();
  };

  return (
    <div className="flex items-center gap-1 p-1 border rounded-md bg-background mb-2">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => handleFormat('bold')}
            >
              <Bold className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Bold text</p>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => handleFormat('italic')}
            >
              <Italic className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Italic text</p>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2"
                >
                  😊
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0">
                <Picker
                  data={data}
                  onEmojiSelect={onEmojiSelect}
                  theme="light"
                />
              </PopoverContent>
            </Popover>
          </TooltipTrigger>
          <TooltipContent>
            <p>Add emoji</p>
          </TooltipContent>
        </Tooltip>

        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          accept="image/*,video/mp4,video/quicktime"
          onChange={handleFileChange}
        />

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => fileInputRef.current?.click()}
            >
              <Paperclip className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Attach image or video</p>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2 bg-white hover:bg-primary/5 
                border-2 border-primary/50 hover:border-primary
                shadow-sm hover:shadow-md hover:shadow-primary/20
                transition-all duration-300 
                hover:scale-105
                group"
              onClick={onAIClick}
            >
              <Brain className="h-4 w-4 text-primary transition-transform duration-300 group-hover:rotate-12" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Generate content with AI</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};